// src/components/ProductModal.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Minus, Plus, X as CloseIcon } from "lucide-react";
import { trn, useI18n } from "../shared/i18n";
import { buildAllergenIconUrl, normalizeProductAllergens, type ProductAllergen } from "../allergens";

/** ===== Tipos del producto ===== */
type RawOption = { id?: string | number; name?: string; price?: number; image?: string; isAvailable?: boolean; available?: boolean };
type RawCategory = {
  id?: string | number;
  name?: string;
  minSelection?: number;
  maxSelection?: number;
  min?: number;
  max?: number;
  maxSameOptionSelection?: number;
  productOptions?: RawOption[];
  options?: RawOption[];
};

export type ModalProduct = {
  id: string | number;
  name: string;
  price?: number;
  largeImage?: string;
  image?: string;
  shortDescription?: string;
  allergens?: ProductAllergen[];
  productOptionCategories?: RawCategory[];
  options?: RawCategory[]; // tolerante con otras claves
};

export type ModalAddPayload = {
  productId: string | number;
  name: string;
  unitPrice: number;      // precio unidad con opciones
  qty: number;
  note: string | null;
  // Opciones elegidas
  options: Array<{
    catId: string | number;
    catName: string;
    optionId: string | number;
    optionName: string;
    unitPrice: number;
    qty: number;          // veces elegida esa opción
  }>;
};

const ACCENT = "var(--dq-accent, #FFD700)";
const ACCENT_TEXT = "var(--dq-button-text, #111111)";
const CTA_WIDTH = 240;

/** Normaliza categorías/opciones desde distintos esquemas */
function normalizeCategories(p?: ModalProduct) {
  const cats = (p?.productOptionCategories ?? p?.options ?? []) as RawCategory[];
  return (cats || []).map((c, idx) => ({
    id: (c.id ?? `cat_${idx}`) as string | number,
    name: c.name ?? trn("app.consumer.product_modal.options_fallback", "Options {index}", { index: idx + 1 }),
    min: typeof c.minSelection === "number" ? c.minSelection : c.min ?? 0,
    max: typeof c.maxSelection === "number" ? c.maxSelection : (Number.isFinite(c.max as any) ? (c.max as number) : Infinity),
    maxSameOptionSelection: Number.isFinite(c.maxSameOptionSelection as any) ? (c.maxSameOptionSelection as number) : Infinity,
    options: (c.productOptions ?? c.options ?? []).map((o, j) => ({
      id: (o.id ?? `opt_${idx}_${j}`) as string | number,
      name: o.name ?? trn("app.consumer.product_modal.option_fallback", "Option {index}", { index: j + 1 }),
      price: Number(o.price ?? 0),
      available: o.isAvailable ?? o.available ?? true,
      image: o.image ?? undefined,
    })),
  }));
}
function sumMapQty(m?: Map<string | number, number>) {
  if (!m) return 0;
  let s = 0;
  for (const v of m.values()) s += v;
  return s;
}

/** ====== Componente ====== */
export default function ProductModal({
  product,
  onClose,
  onAdd,
}: {
  product: ModalProduct;
  onClose: () => void;
  onAdd: (payload: ModalAddPayload) => void;
}) {
  const { language } = useI18n();
  const cats = useMemo(() => normalizeCategories(product), [product]);
  const hasCats = cats.length > 0;
  const isSimpleProduct = !hasCats;
  const allergenCodes = useMemo(
    () => normalizeProductAllergens(product.allergens ?? [], language),
    [product.allergens, language]
  );

  const [step, setStep] = useState(0);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [counts, setCounts] = useState<Map<string | number, Map<string | number, number>>>(new Map());
  const [visible, setVisible] = useState(false);
  const [isLandscapeLayout, setIsLandscapeLayout] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth >= 1100 && window.innerWidth > window.innerHeight;
  });
  const bodyScrollRef = useRef<HTMLDivElement | null>(null);

  // cerrar con ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => (e.key === "Escape" ? onClose() : undefined);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const syncLayout = () => {
      setIsLandscapeLayout(window.innerWidth >= 1100 && window.innerWidth > window.innerHeight);
    };
    syncLayout();
    window.addEventListener("resize", syncLayout);
    return () => window.removeEventListener("resize", syncLayout);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    bodyScrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [step]);

  /** Precio unitario = base + opts */
  const unitPrice = useMemo(() => {
    let base = Number(product.price ?? 0);
    for (const c of cats) {
      const m = counts.get(c.id);
      if (!m) continue;
      for (const [optId, times] of m.entries()) {
        const opt = c.options.find((o) => String(o.id) === String(optId));
        if (!opt) continue;
        base += Number(opt.price || 0) * times;
      }
    }
    return base;
  }, [product, cats, counts]);

  const atLast = step >= cats.length;
  const showCheckoutFooter = !hasCats || atLast;
  const currentCat = !atLast ? cats[step] : null;
  const currentMap = currentCat ? counts.get(currentCat.id) ?? new Map() : null;
  const isPortraitLayout = !isLandscapeLayout;
  const modalWidth = isLandscapeLayout
    ? (isSimpleProduct ? "min(1100px, 92vw)" : "min(1220px, 92vw)")
    : "min(760px, 92vw)";
  const modalHeight = isLandscapeLayout ? "min(860px, 90vh)" : "min(1080px, 88vh)";
  const currentCatRules = useMemo(() => {
    if (!currentCat) return [];
    const rules: string[] = [];
    const min = currentCat.min ?? 0;
    const max = currentCat.max;

    if (min > 0) {
      rules.push(
        trn("app.consumer.product_modal.selection_rules_min_value", "mín. {min}", {
          min,
        })
      );
    }

    if (min > 0 && Number.isFinite(max)) {
      rules.push(
        trn("app.consumer.product_modal.selection_rules_max_value", "máx. {max}", {
          max,
        })
      );
    }

    return rules;
  }, [currentCat]);

  function isCatValid(c: (typeof cats)[number]) {
    const total = sumMapQty(counts.get(c.id));
    return total >= (c.min ?? 0);
  }
  const allValid = useMemo(() => cats.every(isCatValid), [cats, counts]);

  function changeQty(catId: string | number, optId: string | number, delta: 1 | -1) {
    const c = cats.find((x) => String(x.id) === String(catId));
    if (!c) return;

    setCounts((prev) => {
      const next = new Map(prev);
      const m = new Map(next.get(catId) ?? []);
      const curr = m.get(optId) ?? 0;

      const maxCat = Number.isFinite(c.max) ? (c.max as number) : Infinity;
      const maxPer = Number.isFinite(c.maxSameOptionSelection) ? (c.maxSameOptionSelection as number) : Infinity;
      const total = sumMapQty(m);

      if (delta === 1) {
        if (total >= maxCat || curr >= maxPer) return prev;
        m.set(optId, curr + 1);
      } else {
        const v = Math.max(0, curr - 1);
        if (v === 0) m.delete(optId);
        else m.set(optId, v);
      }
      next.set(catId, m);
      return next;
    });
  }

  function pickSingle(catId: string | number, optId: string | number) {
    setCounts((prev) => {
      const next = new Map(prev);
      next.set(catId, new Map([[optId, 1]]));
      return next;
    });
    // si min<=1, pasamos al siguiente paso
    const c = cats.find((x) => String(x.id) === String(catId));
    if (c && (c.min ?? 0) <= 1) setTimeout(() => setStep((s) => Math.min(s + 1, cats.length)), 120);
  }

  function next() {
    if (atLast) return;
    const c = cats[step];
    if (!c || isCatValid(c)) setStep((s) => s + 1);
  }
  function prev() {
    setStep((s) => Math.max(0, s - 1));
  }

  function handleAdd() {
    if (!allValid) return;
    const chosen: ModalAddPayload["options"] = [];
    for (const c of cats) {
      const m = counts.get(c.id);
      if (!m) continue;
      for (const [optId, times] of m.entries()) {
        if (times <= 0) continue;
        const opt = c.options.find((o) => String(o.id) === String(optId));
        if (!opt) continue;
        chosen.push({
          catId: c.id,
          catName: c.name,
          optionId: opt.id!,
          optionName: opt.name!,
          unitPrice: Number(opt.price || 0),
          qty: times,
        });
      }
    }
    onAdd({
      productId: product.id!,
      name: product.name,
      unitPrice,
      qty,
      note: note || null,
      options: chosen,
    });
    onClose();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
      aria-modal
      role="dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: modalWidth,
          height: modalHeight,
          background: "#fff",
          color: "#111",
          borderRadius: 28,
          overflow: "hidden",
          display: "flex",
          flexDirection: isLandscapeLayout ? "row" : "column",
          boxShadow: "0 14px 48px rgba(0,0,0,.25)",
          position: "relative",
          transform: visible ? "translateY(0)" : "translateY(24px)",
          opacity: visible ? 1 : 0,
          transition: "transform .26s ease, opacity .2s ease",
        }}
      >
        <div
          style={{
            background: "#f7ead0",
            flex: isLandscapeLayout ? (isSimpleProduct ? "0 0 40%" : "0 0 43%") : "0 0 38%",
            minWidth: isLandscapeLayout ? (isSimpleProduct ? 320 : 360) : 0,
            maxWidth: isLandscapeLayout ? (isSimpleProduct ? 460 : 520) : "100%",
            minHeight: 0,
            display: "flex",
            alignItems: "stretch",
            padding: isLandscapeLayout ? 24 : 0,
            overflow: "hidden",
          }}
        >
          {(product.largeImage || product.image) && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: isLandscapeLayout ? 18 : 12,
                minHeight: 0,
              }}
            >
              <div
                style={{
                  width: "100%",
                  flex: "0 0 auto",
                  overflow: "hidden",
                  background: "#fff7e8",
                  borderRadius: isLandscapeLayout ? 22 : 0,
                }}
              >
                <img
                  src={product.largeImage || product.image}
                  alt={product.name}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                    objectPosition: "center",
                  }}
                />
              </div>
              {product.shortDescription ? (
                <p
                  style={{
                    margin: 0,
                    padding: isLandscapeLayout ? "0 6px" : "0 18px 12px",
                    color: "#3b3b3b",
                    fontSize: isLandscapeLayout ? 24 : 18,
                    fontWeight: 600,
                    lineHeight: 1.32,
                    textWrap: "balance",
                    flex: "0 0 auto",
                  }}
                >
                  {product.shortDescription}
                </p>
              ) : null}
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          aria-label={trn("app.consumer.common.close", "Close")}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 44,
            height: 44,
            borderRadius: 14,
            border: "1px solid rgba(17,17,17,0.08)",
            background: "#ffffff",
            color: "#111",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            cursor: "pointer",
            boxShadow: "0 8px 18px rgba(17,17,17,0.10)",
            zIndex: 2,
            padding: 0,
            flexShrink: 0,
          }}
        >
          <CloseIcon
            size={24}
            strokeWidth={2.75}
            absoluteStrokeWidth
            aria-hidden
            style={{
              display: "block",
              width: 24,
              height: 24,
              flexShrink: 0,
              strokeLinecap: "round",
              strokeLinejoin: "round",
            }}
          />
        </button>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "grid",
            gridTemplateRows: "auto minmax(0, 1fr) auto",
          }}
        >
          {/* body */}
          <div
            ref={bodyScrollRef}
            style={{
              padding: isLandscapeLayout ? "20px 20px 16px" : "18px 18px 14px",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              gap: hasCats ? 0 : 18,
              minHeight: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, paddingRight: 56 }}>
              <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{product.name}</h3>
              <strong className="dq-price-text" style={{ fontSize: 18 }}>{unitPrice.toFixed(2)} €</strong>
            </div>

            {allergenCodes.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                {allergenCodes.map((allergen) => (
                  <span
                    key={allergen.code}
                    data-allergen-code={allergen.code}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      minHeight: 30,
                      padding: "6px 10px",
                      borderRadius: 999,
                      border: "1px solid rgba(17,17,17,0.1)",
                      background: "#f6f6f6",
                      color: "#333",
                      fontSize: 13,
                      fontWeight: 700,
                      gap: 8,
                    }}
                  >
                    <img
                      src={buildAllergenIconUrl(allergen.code)}
                      alt=""
                      aria-hidden
                      style={{ width: 18, height: 18, objectFit: "contain", flexShrink: 0 }}
                      loading="lazy"
                    />
                    {allergen.label}
                  </span>
                ))}
              </div>
            ) : null}

            {/* Wizard de opciones */}
            {hasCats && !atLast && currentCat && (
              <>
                <div
                  style={{
                    height: 6,
                    borderRadius: 999,
                    background: "#eee",
                    overflow: "hidden",
                    margin: "14px 72px 14px 0",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${((step + 1) / cats.length) * 100}%`,
                      background: ACCENT,
                    }}
                  />
                </div>

                <h4 style={{ margin: "6px 0 10px", fontSize: 18 }}>
                  {currentCat.name}{" "}
                  {currentCatRules.length ? (
                    <span style={{ color: "#777", fontWeight: 400 }}>
                      ({currentCatRules.join(" · ")})
                    </span>
                  ) : null}
                </h4>

                <div style={{ display: "grid", gap: 10 }}>
                  {currentCat.options.map((o) => {
                    const m = currentMap!;
                    const q = m.get(o.id!) ?? 0;
                    const isRadio = currentCat.max === 1 && currentCat.maxSameOptionSelection === 1;
                    const total = sumMapQty(currentMap!);
                    const canPlus = o.available && (isRadio ? q === 0 : total < (currentCat.max ?? Infinity)) && q < (currentCat.maxSameOptionSelection ?? Infinity);
                    const canMinus = q > 0;
                    return (
                      <div
                        key={String(o.id)}
                        style={{
                          display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 10,
                          padding: 12, borderRadius: 12,
                          border: `1px solid ${q ? ACCENT : "#eaeaea"}`,
                          background: q ? "rgba(255,215,0,.10)" : "#fff",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, textAlign: "left" }}>{o.name}</div>
                          {o.price ? (
                            <div
                              className="dq-price-text"
                              style={{ opacity: 0.82, fontSize: 18, fontWeight: 700, textAlign: "left" }}
                            >
                              +{Number(o.price).toFixed(2)} €
                            </div>
                          ) : null}
                        </div>

                        {isRadio ? (
                          <button
                            onClick={() => pickSingle(currentCat.id, o.id!)}
                            disabled={!canPlus}
                            style={btnPrimary(!canPlus)}
                          >
                            {trn("app.consumer.product_modal.choose", "Choose")}
                          </button>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 8px",
                              borderRadius: 999,
                              background: q ? "rgba(255, 215, 0, 0.14)" : "#f8f8f8",
                              border: `1px solid ${q ? "rgba(255, 215, 0, 0.45)" : "#ececec"}`,
                            }}
                          >
                            <button
                              onClick={() => changeQty(currentCat.id, o.id!, -1)}
                            disabled={!canMinus}
                            style={circleBtn(!canMinus)}
                            aria-label={trn("app.consumer.product_modal.remove", "Remove")}
                          >
                              <Minus size={24} strokeWidth={2.75} absoluteStrokeWidth aria-hidden />
                            </button>
                            <div
                              style={{
                                minWidth: 32,
                                textAlign: "center",
                                fontWeight: 900,
                                fontSize: 18,
                                color: q ? "#111" : "#666",
                              }}
                            >
                              {q}
                            </div>
                            <button
                              onClick={() => changeQty(currentCat.id, o.id!, +1)}
                              disabled={!canPlus}
                              style={circleBtn(!canPlus, true)}
                              aria-label={trn("app.consumer.product_modal.add", "Add")}
                            >
                              <Plus size={24} strokeWidth={2.75} absoluteStrokeWidth aria-hidden />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Paso final: notas y cantidad */}
            {(!hasCats || atLast) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 20,
                  marginTop: hasCats ? 6 : 0,
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <div>
                  <label style={{ display: "block", marginBottom: 8, fontWeight: 700 }}>
                    {trn("app.consumer.product_modal.comments", "Comments")}
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    placeholder={trn(
                      "app.consumer.product_modal.comments_placeholder",
                      "E.g. no onion, sauce on the side..."
                    )}
                    style={{
                      width: "100%",
                      minHeight: isSimpleProduct ? 116 : undefined,
                      borderRadius: 16,
                      border: "1px solid #ddd",
                      padding: 14,
                      resize: "vertical",
                      background: "#fff",
                      color: "#111",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                    minWidth: 0,
                    flexWrap: "nowrap",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      borderRadius: 999,
                      background: "#faf7ea",
                      border: "1px solid rgba(255, 215, 0, 0.28)",
                      justifyContent: "center",
                      flex: "0 0 auto",
                    }}
                  >
                    <button
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      disabled={qty <= 1}
                      style={circleBtn(qty <= 1)}
                      aria-label={trn("app.consumer.product_modal.decrease_quantity", "Decrease quantity")}
                    >
                      <Minus size={24} strokeWidth={2.75} absoluteStrokeWidth aria-hidden />
                    </button>
                    <div style={{ minWidth: 40, textAlign: "center", fontWeight: 900, fontSize: 28, lineHeight: 1 }}>
                      {qty}
                    </div>
                    <button
                      onClick={() => setQty((q) => q + 1)}
                      style={circleBtn(false, true)}
                      aria-label={trn("app.consumer.product_modal.increase_quantity", "Increase quantity")}
                    >
                      <Plus size={24} strokeWidth={2.75} absoluteStrokeWidth aria-hidden />
                    </button>
                  </div>
                  <div
                    className="dq-price-text"
                    style={{
                      fontWeight: 900,
                      fontSize: isLandscapeLayout ? 28 : 30,
                      textAlign: "center",
                      minWidth: isLandscapeLayout ? 108 : 0,
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    {(unitPrice * qty).toFixed(2)} €
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* footer */}
          <div
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              background: "#fff",
              borderTop: "1px solid #eee",
              padding: isLandscapeLayout ? 10 : 8,
              paddingBottom: isLandscapeLayout
                ? "max(10px, env(safe-area-inset-bottom))"
                : "max(8px, env(safe-area-inset-bottom))",
              minHeight: isLandscapeLayout ? 108 : undefined,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            {!showCheckoutFooter ? (
              hasCats ? (
                <div
                  style={{
                    display: isLandscapeLayout ? "flex" : "grid",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    gap: 12,
                    marginTop: "auto",
                  }}
                >
                  <button
                    onClick={prev}
                    disabled={step === 0}
                    style={{
                      ...btnSecondary(step === 0),
                      width: isLandscapeLayout ? "160px" : "100%",
                      flex: isLandscapeLayout ? "0 0 160px" : undefined,
                    }}
                  >
                    {trn("app.consumer.product_modal.back", "Back")}
                  </button>
                  {isLandscapeLayout ? <div style={{ flex: 1, minWidth: 0 }} /> : null}
                  <button
                    onClick={next}
                    disabled={!currentCat || !isCatValid(currentCat)}
                    style={{
                      ...btnPrimary(!currentCat || !isCatValid(currentCat), true),
                      justifySelf: "end",
                      width: isLandscapeLayout ? `${CTA_WIDTH}px` : "100%",
                      flex: isLandscapeLayout ? `0 0 ${CTA_WIDTH}px` : undefined,
                    }}
                  >
                    {trn("app.consumer.product_modal.next", "Next")}
                  </button>
                </div>
              ) : null
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  justifyContent: "flex-end",
                  marginTop: "auto",
                }}
              >
                <div
                  style={{
                    display: isLandscapeLayout ? "flex" : "grid",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  {hasCats ? (
                    <button
                      onClick={prev}
                      style={{
                        ...btnSecondary(false),
                        width: isLandscapeLayout ? "160px" : "100%",
                        flex: isLandscapeLayout ? "0 0 160px" : undefined,
                      }}
                    >
                      {trn("app.consumer.product_modal.back", "Back")}
                    </button>
                  ) : isLandscapeLayout ? <div style={{ flex: "0 0 160px" }} /> : null}
                  {isLandscapeLayout ? <div style={{ flex: 1, minWidth: 0 }} /> : null}
                  <button
                    onClick={handleAdd}
                    style={{
                      ...btnPrimary(false, true),
                      justifySelf: "end",
                      width: isLandscapeLayout ? `${CTA_WIDTH}px` : "100%",
                      flex: isLandscapeLayout ? `0 0 ${CTA_WIDTH}px` : undefined,
                    }}
                  >
                    {trn("app.consumer.product_modal.add_to_cart", "Add to cart")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** ===== helpers UI ===== */
function circleBtn(disabled?: boolean, accent?: boolean) {
  return {
    width: 44, height: 44, borderRadius: 999,
    border: `1px solid ${accent ? "transparent" : "#d7d7d7"}`,
    background: disabled ? "#f3f3f3" : (accent ? ACCENT : "#fff"),
    color: disabled ? "#9a9a9a" : (accent ? ACCENT_TEXT : "#111"),
    cursor: disabled ? "not-allowed" : "pointer",
    display: "grid", placeItems: "center",
    padding: 0,
    lineHeight: 0,
    fontWeight: 800,
    boxShadow: disabled ? "none" : (accent ? "0 10px 24px rgba(255, 215, 0, 0.28)" : "0 2px 8px rgba(17,17,17,0.06)"),
    transition: "transform .12s ease, box-shadow .12s ease",
  } as React.CSSProperties;
}
function btnPrimary(disabled?: boolean, wide?: boolean) {
  return {
    border: `1px solid ${ACCENT}`,
    background: disabled ? "#d8d8d8" : ACCENT,
    color: disabled ? "#666" : ACCENT_TEXT,
    padding: "14px 18px",
    borderRadius: 16,
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer",
    minWidth: wide ? 190 : 140,
    textAlign: "center",
    whiteSpace: "nowrap",
    boxShadow: disabled ? "none" : "0 12px 28px rgba(255, 215, 0, 0.24)",
  } as React.CSSProperties;
}
function btnSecondary(disabled?: boolean) {
  return {
    border: "1px solid #ddd",
    background: "#fff",
    color: "#111",
    padding: "14px 18px",
    borderRadius: 16,
    fontWeight: 800,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? .55 : 1,
    minWidth: 120,
    boxShadow: disabled ? "none" : "0 2px 10px rgba(17,17,17,0.05)",
  } as React.CSSProperties;
}
